import defaultLogger from './log'
import { CSRF_PROTECTION_HEADER_NAME, CSRF_PROTECTION_HEADER_VALUE } from './constants'

/**
 * Calls the /api/tokenRefresh endpoint of ATAPI to trigger an explicit token refresh.
 * @param {string} [serverAddress] - Absolute URL prefix (needed for unit tests, because there are no relative URLs
 *                                   allowed) [optional] - format: http[s]://serverName:serverPort
 * @param {object} [logger]        - A logger object to log the error to e.g. console.
 */
export async function refreshAuthToken(serverAddress = '', logger = defaultLogger) {
    const url = `${serverAddress}/api/tokenRefresh`
    const fetchOptions = {
        mode: 'cors',
        credentials: 'same-origin',
        method: 'get',
        headers: {
            [CSRF_PROTECTION_HEADER_NAME]: CSRF_PROTECTION_HEADER_VALUE
        }
    }
    try {
        await fetch(url, fetchOptions)
    } catch (error) {
        logger.error('Error on requesting token refresh', error)
    }
}

/**
 * Scheduler for periodic token refresh.
 * @param {number} delayInMilliseconds - The refresh period. A value of 0 disables the scheduler.
 * @param {string} [serverAddress]     - Absolute URL prefix (needed for unit tests, because there are no relative URLs
 *                                       allowed) [optional] - format: http[s]://serverName:serverPort
 * @param {object} [logger]            - A logger object to log the error to e.g. console.
 */
export function createTokenRefreshScheduler(delayInMilliseconds, serverAddress = '', logger = defaultLogger) {
    let intervalId = null

    if (!Number.isInteger(delayInMilliseconds)) {
        throw new Error('/createTokenRefreshScheduler: parameter `delayInMilliseconds` must be an integer!')
    }

    if (delayInMilliseconds === 0) {
        logger.info('A scheduler period of 0 was provided. This will disable the token refresh scheduler.')
    }

    const start = () => {
        if (delayInMilliseconds === 0) {
            return
        }
        if (intervalId != null) {
            logger.warn('TokenRefresh scheduler is already active. Ignoring start request...')
            return
        }
        intervalId = setInterval(refreshAuthToken, delayInMilliseconds, serverAddress, logger)
    }

    const stop = () => {
        clearInterval(intervalId)
        intervalId = null
    }

    const restart = () => {
        stop()
        start()
    }

    return {
        start,
        stop,
        restart
    }
}
